/**
 * File-manager listing for the Documents page: a table (name / date / size /
 * owner) with per-row checkboxes, sortable columns and a row menu, plus the
 * compact grid view. Rows stay dnd-kit draggable onto the tag sidebar.
 */
import { useMemo } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useDraggable } from '@dnd-kit/core'
import {
  FileText,
  Image as ImageIcon,
  Plus,
  Upload,
  MoreHorizontal,
  Trash2,
  Tags,
  Loader2,
  AlertTriangle,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { isImageDocumentExtension } from '@hub/contracts'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { formatFileSize } from '@/components/document-upload'
import { useDateLocale } from '@/i18n/dates'
import type { DocumentTag } from '@/components/document-tag-sidebar'

const MAX_TAGS = 5

export interface DocumentItem {
  id: string
  projectId: string
  title: string
  slug: string
  tags: Array<{ id: string; name: string; color: string }>
  sourceFilePath?: string | null
  conversionStatus?: 'pending' | 'converting' | 'completed' | 'failed' | null
  sizeBytes?: number | null
  createdByName?: string | null
  createdAt: string
  updatedAt: string
}

export type DocumentSortKey = 'name' | 'date' | 'size'
export interface DocumentSort {
  key: DocumentSortKey
  dir: 'asc' | 'desc'
}

/** Colored file-type tile, like a file manager's icons. */
const BADGES: Record<string, { label: string; className: string }> = {
  pdf: { label: 'PDF', className: 'bg-red-500' },
  doc: { label: 'W', className: 'bg-blue-600' },
  docx: { label: 'W', className: 'bg-blue-600' },
  xls: { label: 'X', className: 'bg-emerald-600' },
  xlsx: { label: 'X', className: 'bg-emerald-600' },
  ppt: { label: 'P', className: 'bg-orange-500' },
  pptx: { label: 'P', className: 'bg-orange-500' },
  csv: { label: 'CSV', className: 'bg-teal-600' },
  txt: { label: 'TXT', className: 'bg-neutral-500' },
}

export function FileBadge({
  sourceFilePath,
  className,
}: {
  sourceFilePath?: string | null
  className?: string
}) {
  const ext = sourceFilePath?.split('.').pop()?.toLowerCase()
  if (ext && isImageDocumentExtension(`.${ext}`)) {
    // Image document (board card 2u86) — a picture glyph instead of a label.
    return (
      <span
        className={cn(
          'grid size-6 shrink-0 place-items-center rounded-md bg-violet-600 text-white',
          className
        )}
      >
        <ImageIcon className="h-3.5 w-3.5" />
      </span>
    )
  }
  const badge = ext ? BADGES[ext] : undefined
  if (!badge) {
    // Editor document — brand yellow tile with a document glyph.
    return (
      <span
        className={cn(
          'grid size-6 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground',
          className
        )}
      >
        <FileText className="h-3.5 w-3.5 text-black/80" />
      </span>
    )
  }
  return (
    <span
      className={cn(
        'grid size-6 shrink-0 place-items-center rounded-md text-[8.5px] font-bold text-white',
        badge.className,
        className
      )}
    >
      {badge.label}
    </span>
  )
}

function ConversionIndicator({ status }: { status: string | null | undefined }) {
  const { t } = useTranslation('documents')
  if (status === 'pending' || status === 'converting') {
    return (
      <Badge variant="secondary" className="gap-1 text-xs">
        <Loader2 className="h-3 w-3 animate-spin" />
        {t('list.conversion.converting')}
      </Badge>
    )
  }
  if (status === 'failed') {
    return (
      <Badge variant="destructive" className="gap-1 text-xs">
        <AlertTriangle className="h-3 w-3" />
        {t('list.conversion.failed')}
      </Badge>
    )
  }
  return null
}

function TagPills({ tags }: { tags: Array<{ id: string; name: string; color: string }> }) {
  if (tags.length === 0) return null
  return (
    <div className="flex items-center gap-1">
      {tags.map((tag) => (
        <span
          key={tag.id}
          className="inline-flex items-center gap-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
        >
          <span
            className="h-2.5 w-2.5 flex-shrink-0 rounded-full ring-1 ring-black/10"
            style={{ backgroundColor: tag.color }}
          />
          {tag.name}
        </span>
      ))}
    </div>
  )
}

/** Initials chip for the Owner column. */
function OwnerChip({ name }: { name: string | null | undefined }) {
  if (!name) return <span className="text-muted-foreground/50">—</span>
  const initials = name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
  return (
    <span
      title={name}
      className="grid size-7 place-items-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground"
    >
      {initials}
    </span>
  )
}

interface DocumentListProps {
  documents: DocumentItem[]
  allTags: DocumentTag[]
  projectId: string
  sort: DocumentSort
  onSortChange: (sort: DocumentSort) => void
  viewMode: 'list' | 'grid'
  selectedIds: Set<string>
  onToggleSelect: (docId: string, shiftKey: boolean) => void
  onToggleSelectAll: () => void
  isLoading: boolean
  hasSearch: boolean
  onSetTags: (docId: string, tagIds: string[]) => void
  onDeleteDocument: (doc: DocumentItem) => void
  onReindexDocument?: (doc: DocumentItem) => void
  onCreateDocument: () => void
  onOpenPicker: () => void
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSortChange,
  className,
}: {
  label: string
  sortKey: DocumentSortKey
  sort: DocumentSort
  onSortChange: (sort: DocumentSort) => void
  className?: string
}) {
  const active = sort.key === sortKey
  const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ChevronsUpDown
  return (
    <button
      type="button"
      onClick={() =>
        onSortChange(
          active
            ? { key: sortKey, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
            : { key: sortKey, dir: 'asc' }
        )
      }
      className={cn(
        'flex items-center gap-1 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground',
        active && 'text-foreground',
        className
      )}
    >
      {label}
      <Icon className="h-3 w-3" />
    </button>
  )
}

export function DocumentList({
  documents,
  allTags,
  projectId,
  sort,
  onSortChange,
  viewMode,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  isLoading,
  hasSearch,
  onSetTags,
  onDeleteDocument,
  onReindexDocument,
  onCreateDocument,
  onOpenPicker,
}: DocumentListProps) {
  const { t } = useTranslation('documents')

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-sm text-muted-foreground">{t('list.loading')}</p>
      </div>
    )
  }

  if (documents.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="space-y-3 text-center">
          <FileText className="mx-auto h-12 w-12 text-muted-foreground/40" />
          <p className="text-muted-foreground">
            {hasSearch ? t('list.noSearchResults') : t('list.empty')}
          </p>
          {!hasSearch && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" onClick={onOpenPicker} className="gap-2">
                <Upload className="h-4 w-4" />
                {t('list.uploadFiles')}
              </Button>
              <Button variant="outline" onClick={onCreateDocument} className="gap-2">
                <Plus className="h-4 w-4" />
                {t('list.createDocument')}
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (viewMode === 'grid') {
    return (
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {documents.map((doc) => (
            <GridDocumentCard
              key={doc.id}
              doc={doc}
              projectId={projectId}
              allTags={allTags}
              isSelected={selectedIds.has(doc.id)}
              onToggleSelect={onToggleSelect}
              onSetTags={onSetTags}
              onDeleteDocument={onDeleteDocument}
              onReindexDocument={onReindexDocument}
            />
          ))}
        </div>
      </div>
    )
  }

  const allSelected = documents.length > 0 && documents.every((d) => selectedIds.has(d.id))
  const someSelected = documents.some((d) => selectedIds.has(d.id))

  return (
    <div className="flex-1 overflow-y-auto px-4 pb-4">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead className="sticky top-0 z-10 bg-background">
          <tr>
            <th className="w-10 border-b border-border py-2 pl-2 text-left">
              <Checkbox
                aria-label={t('list.selectAll')}
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={onToggleSelectAll}
              />
            </th>
            <th className="border-b border-border py-2 text-left">
              <SortHeader
                label={t('list.columns.name')}
                sortKey="name"
                sort={sort}
                onSortChange={onSortChange}
              />
            </th>
            <th className="hidden w-36 border-b border-border py-2 text-left md:table-cell">
              <SortHeader
                label={t('list.columns.date')}
                sortKey="date"
                sort={sort}
                onSortChange={onSortChange}
              />
            </th>
            <th className="hidden w-24 border-b border-border py-2 text-left sm:table-cell">
              <SortHeader
                label={t('list.columns.size')}
                sortKey="size"
                sort={sort}
                onSortChange={onSortChange}
              />
            </th>
            <th className="hidden w-20 border-b border-border py-2 text-left text-xs font-semibold text-muted-foreground lg:table-cell">
              {t('list.columns.owner')}
            </th>
            <th className="w-12 border-b border-border py-2" />
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => (
            <DocumentTableRow
              key={doc.id}
              doc={doc}
              projectId={projectId}
              allTags={allTags}
              isSelected={selectedIds.has(doc.id)}
              onToggleSelect={onToggleSelect}
              onSetTags={onSetTags}
              onDeleteDocument={onDeleteDocument}
              onReindexDocument={onReindexDocument}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface RowProps {
  doc: DocumentItem
  projectId: string
  allTags: DocumentTag[]
  isSelected: boolean
  onToggleSelect: (docId: string, shiftKey: boolean) => void
  onSetTags: (docId: string, tagIds: string[]) => void
  onDeleteDocument: (doc: DocumentItem) => void
  onReindexDocument?: (doc: DocumentItem) => void
}

function DocumentTableRow({
  doc,
  projectId,
  allTags,
  isSelected,
  onToggleSelect,
  onSetTags,
  onDeleteDocument,
  onReindexDocument,
}: RowProps) {
  const { intl } = useDateLocale()
  const navigate = useNavigate()
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: doc.id,
    data: { type: 'document', document: doc },
  })

  return (
    <tr
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-doc-id={doc.id}
      onClick={(e) => {
        if (
          (e.target as HTMLElement).closest(
            'a, button, [role="menu"], [role="menuitem"], [role="checkbox"], label'
          )
        )
          return
        void navigate({ to: '/$projectId/write/$docId', params: { projectId, docId: doc.id } })
      }}
      className={cn(
        'group cursor-pointer touch-none transition-colors select-none hover:bg-accent/50',
        isDragging && 'opacity-40',
        isSelected && 'bg-accent/40'
      )}
    >
      <td className="border-b border-border/60 py-2.5 pl-2 align-middle">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => {}}
          onClick={(e) => {
            e.preventDefault()
            onToggleSelect(doc.id, e.shiftKey)
          }}
        />
      </td>
      <td className="border-b border-border/60 py-2.5 pr-3 align-middle">
        <div className="flex min-w-0 items-center gap-3">
          <FileBadge sourceFilePath={doc.sourceFilePath} />
          <Link
            to="/$projectId/write/$docId"
            params={{ projectId, docId: doc.id }}
            className="truncate font-medium text-foreground hover:underline"
          >
            {doc.title}
          </Link>
          <TagPills tags={doc.tags} />
          <ConversionIndicator status={doc.conversionStatus} />
        </div>
      </td>
      <td className="hidden border-b border-border/60 py-2.5 align-middle text-muted-foreground md:table-cell">
        {new Date(doc.updatedAt).toLocaleDateString(intl, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })}
      </td>
      <td className="hidden border-b border-border/60 py-2.5 align-middle text-muted-foreground sm:table-cell">
        {doc.sizeBytes != null ? formatFileSize(doc.sizeBytes) : '—'}
      </td>
      <td className="hidden border-b border-border/60 py-2.5 align-middle lg:table-cell">
        <OwnerChip name={doc.createdByName} />
      </td>
      <td className="border-b border-border/60 py-2.5 pr-2 text-right align-middle">
        <RowMenu
          doc={doc}
          allTags={allTags}
          onSetTags={onSetTags}
          onDeleteDocument={onDeleteDocument}
          onReindexDocument={onReindexDocument}
        />
      </td>
    </tr>
  )
}

function RowMenu({
  doc,
  allTags,
  onSetTags,
  onDeleteDocument,
  onReindexDocument,
}: Omit<RowProps, 'projectId' | 'isSelected' | 'onToggleSelect'>) {
  const { t } = useTranslation('documents')
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('list.rowMenu')}
          className="rounded p-1.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:bg-accent data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <TagSubmenu doc={doc} allTags={allTags} onSetTags={onSetTags} />
        {onReindexDocument && doc.sourceFilePath && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onReindexDocument(doc)}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('list.reindex')}
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onDeleteDocument(doc)} className="text-destructive">
          <Trash2 className="mr-2 h-4 w-4" />
          {t('list.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function GridDocumentCard({
  doc,
  projectId,
  allTags,
  isSelected,
  onToggleSelect,
  onSetTags,
  onDeleteDocument,
  onReindexDocument,
}: RowProps) {
  const { intl } = useDateLocale()
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: doc.id,
    data: { type: 'document', document: doc },
  })

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-doc-id={doc.id}
      onClick={(e) => {
        if (
          (e.target as HTMLElement).closest(
            'a, button, [role="menu"], [role="menuitem"], [role="checkbox"]'
          )
        )
          return
        onToggleSelect(doc.id, e.shiftKey)
      }}
      className={cn(
        'group flex cursor-grab touch-none flex-col gap-2 rounded-lg border border-border bg-card p-4 transition-colors select-none hover:bg-accent/50 active:cursor-grabbing',
        isDragging && 'opacity-40',
        isSelected && 'bg-accent/30 ring-2 ring-primary'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <FileBadge sourceFilePath={doc.sourceFilePath} />
        <div className="flex items-center gap-1">
          <ConversionIndicator status={doc.conversionStatus} />
          <RowMenu
            doc={doc}
            allTags={allTags}
            onSetTags={onSetTags}
            onDeleteDocument={onDeleteDocument}
            onReindexDocument={onReindexDocument}
          />
        </div>
      </div>
      <Link
        to="/$projectId/write/$docId"
        params={{ projectId, docId: doc.id }}
        className="min-w-0 flex-1"
      >
        <p className="line-clamp-2 text-sm font-medium text-foreground">{doc.title}</p>
      </Link>
      <TagPills tags={doc.tags} />
      <p className="mt-auto text-[11px] text-muted-foreground">
        {new Date(doc.updatedAt).toLocaleDateString(intl)}
        {doc.sizeBytes != null && ` · ${formatFileSize(doc.sizeBytes)}`}
      </p>
    </div>
  )
}

function TagSubmenu({
  doc,
  allTags,
  onSetTags,
}: {
  doc: DocumentItem
  allTags: DocumentTag[]
  onSetTags: (docId: string, tagIds: string[]) => void
}) {
  const { t } = useTranslation('documents')
  const currentTagIds = new Set(doc.tags.map((tag) => tag.id))

  const toggleTag = (tagId: string) => {
    const newTagIds = currentTagIds.has(tagId)
      ? [...currentTagIds].filter((id) => id !== tagId)
      : [...currentTagIds, tagId]
    onSetTags(doc.id, newTagIds)
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Tags className="mr-2 h-4 w-4" />
        {t('list.manageTags')}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="min-w-48">
        {allTags.length === 0 && (
          <DropdownMenuItem disabled>
            <span className="text-xs text-muted-foreground">{t('list.noTagsYet')}</span>
          </DropdownMenuItem>
        )}
        {allTags.map((tag) => {
          const isChecked = currentTagIds.has(tag.id)
          const isDisabled = !isChecked && currentTagIds.size >= MAX_TAGS
          return (
            <DropdownMenuCheckboxItem
              key={tag.id}
              checked={isChecked}
              disabled={isDisabled}
              onCheckedChange={() => toggleTag(tag.id)}
              onSelect={(e) => e.preventDefault()}
            >
              <span
                className="mr-1 h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: tag.color }}
              />
              {tag.name}
            </DropdownMenuCheckboxItem>
          )
        })}
        {currentTagIds.size >= MAX_TAGS && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <span className="text-[10px] text-muted-foreground">
                {t('list.maxTags', { count: MAX_TAGS })}
              </span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}

export function DocumentDragOverlay({
  document,
  count = 1,
}: {
  document: DocumentItem
  count?: number
}) {
  const { t } = useTranslation('documents')
  return (
    <div className="relative w-max">
      {count > 1 && (
        <>
          <div className="absolute inset-0 translate-x-1.5 translate-y-1.5 rounded-lg border border-border bg-card/80 shadow-sm" />
          <div className="absolute inset-0 translate-x-0.5 translate-y-0.5 rounded-lg border border-border bg-card/90 shadow-sm" />
        </>
      )}
      <div className="relative flex max-w-xs items-center gap-3 rounded-lg border border-primary bg-card px-4 py-3 shadow-lg">
        <FileBadge sourceFilePath={document.sourceFilePath} />
        <p className="truncate text-sm font-medium">{document.title}</p>
        {count > 1 ? (
          <Badge variant="secondary" className="flex-shrink-0 text-xs">
            {t('list.documentCount', { count })}
          </Badge>
        ) : (
          <TagPills tags={document.tags} />
        )}
      </div>
    </div>
  )
}
